import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios'; // ✅ Import axios for better handling
import { UserContext } from '../../UserContext'; // ✅ Import User Context

const Strength = () => {
  const { user } = useContext(UserContext); // ✅ Get user from context
  const category = 'strength'; // ✅ Category for fetching videos
  const [videos, setVideos] = useState([]); // ✅ State to hold videos
  const [loading, setLoading] = useState(true); // ✅ Loading state

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await axios.get(`https://wholeness-forward.onrender.com/api/video/videos/${category}`);
        if (Array.isArray(response.data)) {
          setVideos(response.data);
        }
      } catch (error) {
        console.error('Error fetching videos:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  // ✅ Handle Mark as Helpful
  const handleMarkHelpful = async (videoId) => {
    if (!user || !user.id) {
      alert('You must be logged in to mark as helpful.');
      return;
    }

    try {
      const response = await axios.post(`https://wholeness-forward.onrender.com/api/video/videos/toggleHelpful`, {
        videoId,
        userId: user.id,
      });

      // ✅ Update UI with new helpful count
      setVideos((prevVideos) =>
        prevVideos.map((video) =>
          video._id === videoId
            ? { ...video, helpfulCount: response.data.helpfulCount }
            : video
        )
      );
    } catch (error) {
      console.error('Error marking video as helpful:', error);
    }
  };

  return (
    <div className="strength-page page-container">
            <p class="disclaimer">
          Disclaimer: As an Amazon Associate, I earn from qualifying purchases. If you make a purchase through the links here, 
          I may earn a small commission at no extra cost to you. Thank you for your support.
          </p>
          <div className="micro-void"></div>
      <h1 className="page-title">Strength Training</h1>

      <img src="/images/wf-strength.webp" alt="Strength training" className="page-image" width="330" />

      <p className="page-description">
        Strength training, or resistance training, involves exercises that build muscle strength,
        endurance, and power. Using weights, resistance bands, or bodyweight, strength exercises
        enhance physical performance, improve bone density, and support joint health.
      </p>

      <p className="page-description">
        Beyond physical benefits, strength training also contributes to mental resilience and boosts
        overall well-being. Whether you’re new to fitness or a seasoned athlete, strength training is
        adaptable to all levels, offering countless options from bodyweight squats to heavy lifting.
      </p>

      <h2 className="section-title">Health Benefits of Strength Training</h2>
      <p className="section-description">Regular strength training leads to:</p>
      <ul className="content-list">
        <li>Improved muscle strength and endurance</li>
        <li>Increased metabolic rate for better weight management</li>
        <li>Enhanced bone density and reduced risk of osteoporosis</li>
        <li>Better joint health and improved posture</li>
        <li>Boosted mood and increased confidence</li>
      </ul>
      <hr className="section-divider" />

      <h2 className="section-title">Create a Strength Training Habit Plan</h2>
      <p className="section-description">
        Incorporating strength training into your routine is a great way to build muscle, improve health,
        and stay fit. Here's a simple framework to get started:
      </p>
      <h3 className="content-subtitle">Framework for Building Strength Training Habits:</h3>
      <ul className="content-list">
        <li><strong>Start Small:</strong> Begin with lighter weights or bodyweight exercises.</li>
        <li><strong>Consistency is Key:</strong> Aim to work on strength at least 2-3 times a week.</li>
        <li><strong>Track Progress:</strong> Use a fitness app or journal to track your reps and weights.</li>
      </ul>
      <hr className="section-divider" />

      {/* Curated Video Collection */}
      <h2 className="section-title">Curated Video Collection</h2>

      {loading ? (
        <p className="loading-message">Loading videos...</p>
      ) : (
        videos.length > 0 ? (
          videos.map((video) => (
            <div key={video._id} className="video-card">
              <h3 className="video-title">{video.title}</h3>
              <p className="video-details">
                <strong>Source:</strong> {video.source} <br />
                <strong>Length:</strong> {video.length} <br />
                {video.description}
              </p>
              <iframe
                className="video-frame"
                src={video.url}
                title={video.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>

              {/* ✅ Helpful Button */}
              <div className="video-actions">
                <button
                  onClick={() => handleMarkHelpful(video._id)}
                  disabled={video.usersWhoClickedHelpful?.includes(user?.id)}
                  className={`button ${video.usersWhoClickedHelpful?.includes(user?.id) ? "disabled" : ""}`}
                >
                  {video.usersWhoClickedHelpful?.includes(user?.id)
                    ? `Helpful (${video.helpfulCount})`
                    : `Mark as Helpful (${video.helpfulCount || 0})`}
                </button>
              </div>
            </div>
          ))
        ) : (
          <p className="no-videos-message">No videos found for this category.</p>
        )
      )}

      

<hr className="section-divider" />
<h2 className="section-title">Curated Book Collection</h2>

{/* Book 1: Strength Training Anatomy */}
<div className="book-card">
  <h3 className="book-title">Strength Training Anatomy</h3>

  {/* Book Cover Image from Open Library */}
  <img 
    src="https://covers.openlibrary.org/b/id/462291-M.jpg" // Open Library cover image URL
    alt="Strength Training Anatomy by Frederic Delavier" 
    className="book-cover" 
  />
  
  {/* Attribution for Image Source */}
  <p className="image-source">Image Source: Open Library API</p>

  <div className="micro-void"></div>

  <p className="book-details">
    <strong>Author:</strong> Frederic Delavier <br />
    <div className="micro-void"></div>
    <strong>Overview:</strong> *Strength Training Anatomy* provides an in-depth exploration of the muscles involved in various strength exercises. With highly detailed anatomical illustrations, Delavier explains how to target and strengthen different muscle groups through proper technique. <br />
    <div className="barely-void"></div>
    <strong>Why It's Great:</strong> This book offers a visual approach to strength training, making it easy to understand how exercises affect specific muscle groups. It’s perfect for anyone looking to optimize their training and learn about the anatomy behind the exercises.
  </p>

  <div className="micro-void"></div>

  <a
    href="https://amzn.to/4iiwqBU" // Your actual Amazon affiliate link
    className="amazon-link"
    target="_blank"
    rel="noopener noreferrer"
  >
    Buy on Amazon-https://amzn.to/4iiwqBU
  </a>

  {/* Affiliate Link Disclosure */}
  <p className="affiliate-link-disclaimer">As an Amazon Associate, I earn from qualifying purchases.</p>
  <div className="short-line"></div>
</div>

{/* Book 2: Starting Strength: Basic Barbell Training, 3rd Edition */}
<div className="book-card">
  <h3 className="book-title">Starting Strength: Basic Barbell Training, 3rd Edition</h3>

  {/* Book Cover Image from Open Library */}
  <img 
    src="https://covers.openlibrary.org/b/id/8722490-M.jpg" // Open Library cover image URL
    alt="Starting Strength: Basic Barbell Training, 3rd Edition by Mark Rippetoe" 
    className="book-cover" 
  />
  
  {/* Attribution for Image Source */}
  <p className="image-source">Image Source: Open Library API</p>

  <div className="micro-void"></div>

  <p className="book-details">
    <strong>Author:</strong> Mark Rippetoe, Jason Kelly <br />
    <div className="micro-void"></div>
    <strong>Overview:</strong> *Starting Strength: Basic Barbell Training* is considered one of the most important fitness books for learning barbell training. It covers the biomechanical principles of strength training and offers detailed, step-by-step instructions for the six basic barbell lifts. This updated third edition expands on the previous methods with improved illustrations and photographs, making it easier for readers to perform exercises safely and effectively. <br />
    <div className="barely-void"></div>
    <strong>Why It's Great:</strong> This book is a must-read for anyone serious about lifting. It provides the most detailed and comprehensive guide to barbell training, making it an invaluable resource for beginners and experienced lifters alike.
  </p>

  <div className="micro-void"></div>

  <a
    href="https://amzn.to/4bFqksO" // Your actual Amazon affiliate link
    className="amazon-link"
    target="_blank"
    rel="noopener noreferrer"
  >
    Buy on Amazon-https://amzn.to/4bFqksO
  </a>

  {/* Affiliate Link Disclosure */}
  <p className="affiliate-link-disclaimer">As an Amazon Associate, I earn from qualifying purchases.</p>
  <div className="short-line"></div>
</div>

{/* Book 3: Built from Broken */}
<div className="book-card">
  <h3 className="book-title">Built from Broken</h3>

  {/* Book Cover Image from Open Library */}
  <img 
    src="https://covers.openlibrary.org/b/id/11974382-M.jpg" // Open Library cover image URL
    alt="Built from Broken by Scott H. Hogan" 
    className="book-cover" 
  />
  
  {/* Attribution for Image Source */}
  <p className="image-source">Image Source: Open Library API</p>

  <div className="micro-void"></div>

  <p className="book-details">
    <strong>Author:</strong> Scott H. Hogan <br />
    <div className="micro-void"></div>
    <strong>Overview:</strong> *Built from Broken* is a science-based guide designed to help individuals heal painful joints, prevent injuries, and rebuild their bodies. It covers the causes of joint pain, offers corrective exercises, and presents a step-by-step training program for enhancing mobility and strength. The book is perfect for those suffering from chronic pain or looking to prevent injuries. <br />
    <div className="barely-void"></div>
    <strong>Why It's Great:</strong> This book provides a fresh approach to joint health and injury recovery. With a focus on corrective exercises and proper body mechanics, it’s an excellent resource for anyone looking to recover from injury and build a stronger, healthier body.
  </p>

  <div className="micro-void"></div>

  <a
    href="https://amzn.to/4bJFLQQ" // Your actual Amazon affiliate link
    className="amazon-link"
    target="_blank"
    rel="noopener noreferrer"
  >
    Buy on Amazon-https://amzn.to/4bJFLQQ
  </a>

  {/* Affiliate Link Disclosure */}
  <p className="affiliate-link-disclaimer">As an Amazon Associate, I earn from qualifying purchases.</p>
</div>


      
      

      <hr className="section-divider" />

      <h2 className="section-title">Popular Strength Training Apps</h2>
      <p className="section-description">
        Strength training apps can help you stay on track and optimize your workouts:
      </p>
      <h3 className="content-subtitle">StrongLifts 5x5</h3>
      <p>A simple and effective app focusing on compound exercises like squats and deadlifts.</p>
      <h3 className="content-subtitle">Fitbod</h3>
      <p>Fitbod helps design personalized strength workouts based on your fitness level.</p>
      <h3 className="content-subtitle">JEFIT</h3>
      <p>JEFIT provides workout plans with tracking for each exercise.</p>

      <hr />

      <h2 className="section-title">Join the Strength Training Discussion</h2>
      <p className="section-description">
        Have questions or insights about strength training? Join our forum to connect with others exploring this topic.
      </p>
      <a href="/forum/Strength" className="forum-link">Go to Strength Forum</a>
    </div>
  );
};

export default Strength;
